import { Constants } from "appConstants";
import { ApplicationData } from "pages/NewApplication/steps/types";
import { AnalysisEventDocument, AnalysisStatusLabel } from "services/types";
import { ObjectWithAnyValues } from "utils/types";

export interface HighwayConcessionData {
	id: string;
	imageUrl: string;
	concessionaire: ConcessionaireProps;
	highway: HighwayProps;
	highwayName?: string;
	highwayCode?: string;
}

export interface HighwayProps {
	id: string;
	name: string;
	code: string;
	jurisdiction: string;
}

export interface ConcessionaireProps {
	id: string;
	name: string;
	concessionStartDate: Date;
	concessionEndDate: Date;
	active: boolean;
	regulatoryAgency: RegulatoryAgencyProps;
}

export interface TollStationProps {
	id: string;
	name: string;
	code: string;
	highwayConcession: HighwayConcessionData;
}

export interface TagCompanyProps {
	id: string;
	name: string;
	code: string;
}

export interface RegulatoryAgencyProps {
	id: string;
	name: string;
	code: string;
}

export interface ExemptionTypeData {
	id: string;
	name: string;
	description: string;
	detailsUrl: string;
	jurisdictionId: string;
}

export interface ApplicationEventData {
	id: string;
	application: ApplicationData;
	type: ApplicationEventType;
	description: string;
	title: string;
	isPublic: boolean;
	analysis: ObjectWithAnyValues; // TODO: Create ApplicationAnalysis model
	createdByGroup: string;
	createdAt: string;
	createdBy: string;
}

export interface AnalysisEventInternalListItem {
	id: string;
	application: ApplicationData;
	type: ApplicationEventType;
	typeLabel: string;
	description: string;
	title: string;
	createdAt: string;
	createdByGroup: Constants.ReadableUserRoleGroups;
	createdBy: {
		id: string;
		name: string;
	};
	applicationAnalysis: null | {
		id: string;
		analysisCode: string;
		status: AnalysisStatusLabel;
	};
	documents: AnalysisEventDocument[];
	tollIsentos?: TollStationProps[];
}

export enum ApplicationStatus {
	RASCUNHO = "RASCUNHO",
	ANALISTA_PENDENTE = "ANALISTA_PENDENTE",
	ANALISTA_ANDAMENTO = "ANALISTA_ANDAMENTO",
	SOLICITANTE_PENDENTE = "SOLICITANTE_PENDENTE",
	SOLICITANTE_RESPOSTA = "SOLICITANTE_RESPOSTA",
	PENDENTE_CANCELAMENTO = "PENDENTE_CANCELAMENTO",
	CANCELADO = "CANCELADO",
	APROVADO = "APROVADO",
	REPROVADO = "REPROVADO"
}

export const ApplicationFinalStatus = [ApplicationStatus.CANCELADO, ApplicationStatus.REPROVADO];

export enum InternalUserStatusLabel {
	RASCUNHO = "Rascunho",
	PENDENTE = "Pendente",
	ANDAMENTO = "Em andamento",
	MENSAGEM_ENVIADA = "Mensagem enviada",
	MENSAGEM_RECEBIDA = "Mensagem recebida",
	PENDENTE_CANCELAMENTO = "Aguardando cancelamento",
	CANCELADO = "Cancelado",
	APROVADO = "Aprovado",
	REPROVADO = "Reprovado"
}

export const ApplicationStatusInternalLabel: Record<ApplicationStatus, InternalUserStatusLabel> = {
	RASCUNHO: InternalUserStatusLabel.RASCUNHO,
	ANALISTA_PENDENTE: InternalUserStatusLabel.PENDENTE,
	ANALISTA_ANDAMENTO: InternalUserStatusLabel.ANDAMENTO,
	SOLICITANTE_PENDENTE: InternalUserStatusLabel.MENSAGEM_ENVIADA,
	SOLICITANTE_RESPOSTA: InternalUserStatusLabel.MENSAGEM_RECEBIDA,
	PENDENTE_CANCELAMENTO: InternalUserStatusLabel.PENDENTE_CANCELAMENTO,
	CANCELADO: InternalUserStatusLabel.CANCELADO,
	APROVADO: InternalUserStatusLabel.APROVADO,
	REPROVADO: InternalUserStatusLabel.REPROVADO
} as const;

export enum ExternalUserStatusLabels {
	RASCUNHO = "Rascunho",
	ANDAMENTO = "Em andamento",
	MENSAGEM_RECEBIDA = "Mensagem recebida",
	PENDENTE_CANCELAMENTO = "Aguardando cancelamento",
	CANCELADO = "Cancelado",
	APROVADO = "Aprovado",
	REPROVADO = "Reprovado"
}

export const ApplicationStatusExternalLabel: Record<ApplicationStatus, ExternalUserStatusLabels> = {
	RASCUNHO: ExternalUserStatusLabels.RASCUNHO,
	ANALISTA_PENDENTE: ExternalUserStatusLabels.ANDAMENTO,
	ANALISTA_ANDAMENTO: ExternalUserStatusLabels.ANDAMENTO,
	SOLICITANTE_PENDENTE: ExternalUserStatusLabels.MENSAGEM_RECEBIDA,
	SOLICITANTE_RESPOSTA: ExternalUserStatusLabels.ANDAMENTO,
	PENDENTE_CANCELAMENTO: ExternalUserStatusLabels.PENDENTE_CANCELAMENTO,
	APROVADO: ExternalUserStatusLabels.APROVADO,
	REPROVADO: ExternalUserStatusLabels.REPROVADO,
	CANCELADO: ExternalUserStatusLabels.CANCELADO
} as const;

export enum ApplicationEventType {
	SOLICITACAO_CRIADA = "SOLICITACAO_CRIADA",
	ASSUMIR_RESPONSABILIDADE = "ASSUMIR_RESPONSABILIDADE",
	REMOVER_RESPONSABILIDADE = "REMOVER_RESPONSABILIDADE",
	ADICIONAR_COMENTARIO = "ADICIONAR_COMENTARIO",
	SOLICITAR_ALTERACAO = "SOLICITAR_ALTERACAO",
	DEVOLUTIVA_DO_SOLICITANTE = "DEVOLUTIVA_DO_SOLICITANTE",
	SOLICITACAO_CANCELADA = "SOLICITACAO_CANCELADA",
	SOLICITACAO_APROVADA = "SOLICITACAO_APROVADA",
	SOLICITACAO_REPROVADA = "SOLICITACAO_REPROVADA",
	RESPOSTA_INTERESSADO = "RESPOSTA_INTERESSADO",
	SOLICITAR_CANCELAMENTO = "SOLICITAR_CANCELAMENTO",
	APROVAR_CANCELAMENTO = "APROVAR_CANCELAMENTO",
	REJEITAR_CANCELAMENTO = "REJEITAR_CANCELAMENTO"
}

export enum AnalysisStatusCode {
	EM_ANDAMENTO = "EM_ANDAMENTO",
	APROVADO = "APROVADO",
	SOLICITADA_ALTERACAO = "SOLICITADA_ALTERACAO",
	REPROVADO = "REPROVADO",
	CANCELADO = "CANCELADO"
}

export enum AnalysisStatusCodeRequest {
	APROVADO = "APROVADO",
	SOLICITADA_ALTERACAO = "SOLICITADA_ALTERACAO",
	REPROVADO = "REPROVADO"
}

export interface ExemptionMotiveData {
	id: string;
	name: string;
	code: string;
	description: string;
}

export interface ExemptionMotiveTypeData {
	id: string;
	name: string;
	code: string;
	description: string;
}

export interface VehicleCategoryData {
	id: string;
	name: string;
	code: string;
}
